import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { FORM_CHROME_DENSITY } from '@/shared/form/formChrome'
import { formatNumberDisplay } from '@/runtime/number/formatNumber'
import {
  computeTotalsValue,
  totalsItemLabel,
  type TotalsItem,
} from '@/shared/form/totalsModel'

export interface TotalsFooterProps {
  orientation: 'horizontal' | 'vertical'
  items: TotalsItem[]
  formValues: Record<string, unknown>
  /** Designer canvas (`false`) keeps a zero-chrome hit target when every item is hidden. */
  preview?: boolean
}

function formatTotal(n: number | null): string {
  return n == null ? '—' : formatNumberDisplay(n, 15, 2)
}

export function TotalsFooter({ orientation, items, formValues, preview = true }: TotalsFooterProps) {
  const visible = [...items]
    .filter((item) => item.visible)
    .sort((a, b) => a.order - b.order)

  if (visible.length === 0) {
    if (preview) return null
    return <Box data-testid="totals-footer-placeholder" sx={{ width: '100%', minHeight: 8 }} />
  }

  return (
    <Paper
      elevation={1}
      data-testid="totals-footer"
      sx={{
        display: 'flex',
        flexDirection: orientation === 'vertical' ? 'column' : 'row',
        flexWrap: orientation === 'horizontal' ? 'wrap' : undefined,
        gap: 2,
        px: 2,
        py: 1.5,
        borderRadius: `${FORM_CHROME_DENSITY.radiusCard}px`,
        width: '100%',
      }}
    >
      {visible.map((item) => {
        const value = formatTotal(computeTotalsValue(item, formValues))
        return (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              flexDirection: orientation === 'vertical' ? 'row' : 'column',
              alignItems: orientation === 'vertical' ? 'baseline' : 'flex-end',
              gap: orientation === 'vertical' ? 1.5 : 0.25,
              minWidth: 0,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {totalsItemLabel(item)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}
            >
              {value}
            </Typography>
          </Box>
        )
      })}
    </Paper>
  )
}
